import definitions from "../definitions";
import settings from "../settings";
import Tile from "./Tile";
import Tilemap from "./Tilemap";
import { texture } from "p3";

export default class TilemapLayer extends PIXI.Container {
    /**
     * @param {number} id
     * @param {string} name
     * @param {array<number>} tiles
     * @param {number} columns
     * @param {number} tileWidth
     * @param {number} tileHeight
     * @param {Tileset} tileset
     * @param {object} collisionData
     */
    constructor(id, name, tiles, columns, tileWidth, tileHeight, tileset, collisionData) {
        super();

        this._id = id;
        this._layerName = name;
        this._tiles = [];
        this._columns = columns;
        this._rows = Math.floor(tiles.length / columns);
        this._tileWidth = tileWidth;
        this._tileHeight = tileHeight;
        this._tileset = tileset;

        this._initLayout(tiles, collisionData)
    }

    /**
     * @param {string} tileName 
     * @param {Tile} tile 
     */
    _initTileEffects(tileName, tile) {
        if (tileName.includes("stones")) {
            tile.emitFx([texture("path_tile_sparkle_attract_fx_00"), texture("path_tile_sparkle_attract_fx_01")], "fx_stepping_stones_attract");
        } 
    }

    /**
     * @param {array<number>} tiles
     * @param {object} collisionData
     * @private
     */
    _initLayout(tiles, collisionData) {
        this._tiles.length = 0;

        for (let i = 0, count = tiles.length; i < count; ++i) {
            let texture = this._tileset.getTexture(tiles[i]);

            if (!texture) {
                texture = PIXI.Texture.EMPTY;
            }

            const tx = i % this._columns;
            const ty = Math.floor(i / this._columns);

            if (!this._tiles[ty]) {
                this._tiles[ty] = [];
            }

            const tile = new Tile(texture);
            tile.position.set((i % this._columns) * this._tileWidth,
                Math.floor(i / this._columns) * this._tileHeight);

            tile.width = this._tileWidth;
            tile.height = this._tileHeight;

            try {
                const name = Tilemap.filenameFromPath(texture.textureCacheIds[0]);
                const collision = collisionData[name];

                this._initTileEffects(name, tile);

                if (collision) {
                    tile.collisionEnabled = true;
                    
                    // this tile has multiple rectangle colliders
                    if (collision.rectangles !== undefined) {
                        for (let i = 0; i < collision.rectangles.length; ++i) {
                            const colliderRectangle = collision.rectangles[i];
                            tile.collisionRectangles.push({
                                "collisionLeft": colliderRectangle.left,
                                "collisionRight": colliderRectangle.right,
                                "collisionTop": colliderRectangle.top,
                                "collisionBottom": colliderRectangle.bottom
                            });
                        }
                    } else {
                        // this tile has a single rectangle collider
                        tile.collisionLeft = collision.left;
                        tile.collisionRight = collision.right;
                        tile.collisionTop = collision.top;
                        tile.collisionBottom = collision.bottom;    
                    }

                    // this tile has a polygon collider
                    if (collision.vertices !== undefined) {
                        tile.collisionType = definitions.COLLISION_TYPE.POLYGON;
                        for (let i = 0; i < collision.vertices.length; ++i) {
                            const vertex = collision.vertices[i];
                            tile.collisionVertices[i] = new PIXI.Point(vertex.x * tile.width, 
                                                                       vertex.y * tile.height);
                        }
                    } else {
                        tile.collisionType = definitions.COLLISION_TYPE.RECT;
                    }
                    
                    tile.initCollider();
                }
            } catch {
                // do nothing
            }

            this._tiles[ty][tx] = tile;
        }
    }

    /**
     * @param {number} tx
     * @param {number} ty
     * @param {number} columns
     * @param {number} rows
     */
    updateBounds(tx, ty, columns, rows) {
        this.removeChildren();

        const left = Math.max(0, tx);
        const up = Math.max(0, ty);
        const right = Math.min(this._columns, left + columns);
        const down = Math.min(this._rows, up + rows);

        for (let y = up; y < down; ++y) {
            for (let x = left; x < right; ++x) {
                const tile = this._tiles[y][x];
                tile.update();
                this.addChild(tile);

                settings.WORLD_ENABLE_COLLISION_DEBUG && (tile.tint = 0xAAAAAA);
            }
        }
    }

    /**
     * @param {PIXI.Rectangle} rect
     * @returns {array<Tile>}
     */
    queryRect(rect) {
        const tiles = [];

        const tx = Math.floor(rect.x / this._tileWidth);
        const ty = Math.floor(rect.y / this._tileHeight);
        const tw = Math.ceil((rect.x + rect.width) / this._tileWidth);
        const th = Math.ceil((rect.y + rect.height) / this._tileHeight);

        const left = Math.max(0, tx);
        const up = Math.max(0, ty);
        const right = Math.min(this._columns, tw);
        const down = Math.min(this._rows, th);

        for (let y = up; y < down; ++y) {
            for (let x = left; x < right; ++x) {
                const tile = this._tiles[y][x];
                tiles.push(tile);

                settings.WORLD_ENABLE_COLLISION_DEBUG && (tile.tint = 0xFFFFFF);
            }
        }
        return tiles;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {PIXI.Point}
     */
    toTile(x, y) {
        return new PIXI.Point(Math.round(x / this._tileWidth), Math.round(y / this._tileHeight));
    }

    /**
     * @param {number} tx
     * @param {number} ty
     * @returns {PIXI.Point}
     */
    fromTile(tx, ty) {
        return new PIXI.Point(tx * this._tileWidth, ty * this._tileHeight);
    }

    /**
     * @returns {number}
     */
    get id() {
        return this._id;
    }

    /**
     * @returns {string}
     */
    get layerName() {
        return this._layerName;
    }

    /**
     * @returns {number}
     */
    get columns() {
        return this._columns;
    }

    /**
     * @returns {number}
     */
    get tileWidth() {
        return this._tileWidth;
    }

    /**
     * @returns {number}
     */
    get tileHeight() {
        return this._tileHeight;
    }

    /**
     * @returns {Tileset}
     */
    get tileset() {
        return this._tileset;
    }
}